import {
    ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg
} from '~commonInfra/contentActions/ActionHelpers';
import { IErrorItem } from '~commonInfra/contentActions/MultipleItemResult';
import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { openReactConfirmationDialog } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Set } from '~commonInfra/utils/Set';
import { workbookTypeOnly } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/IEntityActionHelpers';
import { SearchSuggestionsService } from '~src/spacesPlatform/services/SearchSuggestionsService';
import { currentUserIsAdmin } from '~src/spacesPlatform/utils/MixedContentHelpers';

import { getAppStore } from '../../bootstrap/redux/store/AppStore';
import { Formatters, Messages } from '../../codegen/Localize';
import { L10n } from '../../commonInfra/L10n/L10n';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { ScheduleActions } from '../../schedule/types/ISchedule';
import { IScheduleInfo } from '../../schedule/types/IScheduleInfo';
import { IContentInfo } from '../../spacesPlatform/models/IContentInfo';
import { IWorkbookBase, IWorkbookInfo } from '../../workbook/models/IWorkbookInfo';
import { Workbooks } from '../../workbook/resources/Workbooks';
import {
    EnableAccelerationDialogBody, EnableAccelerationDialogBodyProps, EnableAccelerationDialogState
} from '../components/EnableWorkbookAccelerationDialog';
import { AccelerationTasks } from '../resources/AccelerationTasks';
import {
    IAccelerationNeedsScheduleResult
} from '../serverApi/requests/AccelerationNeedsScheduleRequest';
import { DataAccelerationErrors } from '../services/DataAccelerationErrors';

function confirmEnableAcceleration(content: IContentInfo[], needsSchedule: boolean): Promise<IResult> {
  const modalInstance = openReactActionDialog<EnableAccelerationDialogBodyProps, EnableAccelerationDialogState, IResult>({
    title:  Messages.accelerateWorkbooks(),
    bodyComponent: EnableAccelerationDialogBody,
    initialBodyProps: {
      prompt: getPromptForContent(needsSchedule),
      getQuery: () => getScheduleQuery(),
      needsSchedule: needsSchedule,
    },
    initialDialogState: { schedule: null },
    actionName: Messages.enableAcceleration(),
    action: (_bodyProps: EnableAccelerationDialogBodyProps, dialogState: EnableAccelerationDialogState) => {
      return enableAcceleration(content, dialogState.schedule).then((results: IResult[]) => {
        return ResultHelper.combineResults(results);
      });
    },
    actionDisabled: (_bodyProps: EnableAccelerationDialogBodyProps,
                      dialogState: EnableAccelerationDialogState) => needsSchedule && dialogState.schedule == null,
    dialogWidth: TabDialogWidth.width500px,
    dismissDialogOnActionFailed: false
  });
  return modalInstance.result;
}

function confirmDisableAcceleration(content: IContentInfo[]): Promise<IResult> {

  let dialogBody: string;
  const workbookCount = content.length;
  if(workbookCount === 1) {
    const workbook = content[0];
    dialogBody = Messages.dataAccelerationDisableWorkbookPrompt({
      WORKBOOK_NAME: L10n.elideIfLong(workbook.name)
    });
  } else {
    dialogBody = Messages.dataAccelerationDisableWorkbooksPrompt({
      ITEM_COUNT_LOC: Formatters.formatCount(workbookCount)
    });
  }

  const dialogInstance = openReactConfirmationDialog<IContentInfo[], IResult>({
    title: Messages.disableAcceleration(),
    message: dialogBody,
    actionName: Messages.disableAcceleration(),
    paramsForAction: content,
    isActionDelete: true,
    action: disableAcceleration,
    dialogWidth: TabDialogWidth.width375px
  });
  return dialogInstance.result;
}

function getScheduleQuery() {
  return SearchSuggestionsService.schedulesQuery(ScheduleActions.Data_Acceleration);
}

function enableAcceleration(content: IContentInfo[], schedule: IScheduleInfo) {
  const workbooks = Set.fromIdItemArray(content.filter(i => i.contentType === 'workbook'));
  if (workbooks.size() < 1) {
    return Promise.resolve(null);
  }

  return Workbooks.enableWorkbookAcceleration(workbooks.keys(), schedule===null ? undefined : schedule.id).then((result: IResult) => {
    return ActionHelpers.displayStandardToastsForResult(
      workbooks,
      result,
      {
        success: standardActionSuccessMsg(Messages.accelerationTasksCreateSuccess),
        error: standardActionErrorMsg(Messages.accelerationTasksCreateError),
      },
      (errorItem: IErrorItem<IWorkbookBase>) => DataAccelerationErrors.errorDetailsForWorkbooks(errorItem),
    );
  });
}

function disableAcceleration(content: IContentInfo[]) {
  const workbooks = Set.fromIdItemArray(content.filter(i => i.contentType === 'workbook'));
  if (workbooks.size() < 1) {
    return Promise.resolve(null);
  }

  return Workbooks.disableWorkbookAcceleration(workbooks.keys()).then((result: IResult) => {
    return ActionHelpers.displayStandardToastsForResult(
      workbooks,
      result,
      {
        success: standardActionSuccessMsg(Messages.dataAccelerationDisableSuccess),
        error: standardActionErrorMsg(Messages.dataAccelerationDisableError),
      },
      (errorItem: IErrorItem<IWorkbookBase>) => DataAccelerationErrors.errorDetailsForWorkbooks(errorItem),
    );
  });
}

function getPromptForContent(needsSchedule: boolean): string {
  if (needsSchedule) {
    return Messages.accelerationTasksWithSchedulePrompt();
  } else {
    return Messages.accelerationTasksNoSchedulePrompt();
  }
}

function accelerationNeedsSchedule(workbooks: IContentInfo[]): Promise<boolean> {
  const idArray = workbooks.map(c => c.id);
  return AccelerationTasks.needsSchedule(idArray).then((result: IAccelerationNeedsScheduleResult) => result.needsSchedule);
}

function checkPermission(targetItems: IWorkbookInfo[]): boolean {
  return FeatureFlags.workbookAccelerationUIEnabled &&
        currentUserIsAdmin(getAppStore().getState()) &&
        workbookTypeOnly(targetItems);
}

export const WorkbookAccelerationActions = {
  accelerationNeedsSchedule,
  enableAcceleration,
  disableAcceleration,
  confirmDisableAcceleration,
  confirmEnableAcceleration,
  assurePermission: checkPermission,
};

export type WorkbookAccelerationActions = typeof WorkbookAccelerationActions;
